import React, { useMemo } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { PersonAdd } from '@mui/icons-material';
import { Alert, Box, Button, Chip, CircularProgress, Stack, Tooltip, Typography } from '@mui/material';

import { LUXURY_ESCAPES } from '~/consts/brands';

import * as LoyaltyService from '~/services/LoyaltyService';

import type { CalculateEarnRequest } from '~/types/loyaltyContract';

import { PURCHASE_TYPE_POST_PURCHASE, PurchaseType, SALES_ATTRIBUTION_CS_ERROR } from './constants';
import { mapCustomOfferTypeToLoyaltyProductType } from './loyaltyUtils';

interface CustomOfferLoyaltyCalculatorProps {
  productType: string;
  sellPrice: number;
  currency: string;
  margin: number;
  leServiceFee: number;
  userId?: string;
  salesAttribution?: string;
  brand?: App.Brands;
  purchaseType?: PurchaseType;
}

export default function CustomOfferLoyaltyCalculator({
  productType,
  sellPrice,
  currency,
  margin,
  leServiceFee,
  userId,
  salesAttribution,
  brand,
  purchaseType,
}: CustomOfferLoyaltyCalculatorProps) {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const loyaltyProductType = mapCustomOfferTypeToLoyaltyProductType(productType);
  // Mirrors buildPendingPointsPayload: post-purchase flights quote at customer price even with zero metadata markup.
  const isPostPurchaseFlight = productType === 'flight' && purchaseType === PURCHASE_TYPE_POST_PURCHASE;
  const hasMetadataMarkup = margin > 0 || leServiceFee > 0;

  const shouldCalculate = useMemo(
    () =>
      sellPrice > 0 &&
      !!currency &&
      !!loyaltyProductType &&
      salesAttribution !== SALES_ATTRIBUTION_CS_ERROR &&
      brand === LUXURY_ESCAPES &&
      (hasMetadataMarkup || isPostPurchaseFlight),
    [sellPrice, currency, loyaltyProductType, salesAttribution, brand, hasMetadataMarkup, isPostPurchaseFlight],
  );

  const {
    data: accountData,
    isLoading: isLoadingAccount,
    error: accountError,
  } = useQuery({
    queryKey: ['loyalty', 'account', userId],
    queryFn: async () => {
      if (!userId) {
        return null;
      }
      return await LoyaltyService.getLoyaltyAccount(userId);
    },
    enabled: shouldCalculate && !!userId,
    retry: false,
    staleTime: 60000,
  });

  const hasAccount = accountData?.result?.accountSummary !== null;

  // Create account mutation
  const createAccountMutation = useMutation({
    mutationFn: (customerId: string) =>
      LoyaltyService.createLoyaltyAccount({
        customerId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loyalty', 'account', userId] });
      enqueueSnackbar('Société account created successfully!', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to create Société account: ${error?.message || 'Unknown error'}`, { variant: 'error' });
    },
  });

  const handleCreateAccount = () => {
    if (userId) {
      createAccountMutation.mutate(userId);
    }
  };

  // Calculate points
  const { data, isLoading, error } = useQuery({
    queryKey: ['loyalty', 'calculate-earn', sellPrice, currency, productType, userId],
    queryFn: async () => {
      if (!loyaltyProductType) {
        throw new Error('Invalid product type for loyalty calculation');
      }

      const request: CalculateEarnRequest = {
        customerId: userId,
        publicPrice: sellPrice,
        luxPlusMember: false,
        currency: currency,
        productDetails: {
          loyaltyProductType,
        },
      };

      return await LoyaltyService.calculateEarnPoints(request);
    },
    enabled: shouldCalculate && !!loyaltyProductType,
    retry: false,
  });

  if (!shouldCalculate) {
    return null;
  }

  if (isLoadingAccount && userId) {
    return (
      <Box mt={1} display="flex" alignItems="center" gap={1}>
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">
          Checking Société account...
        </Typography>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box mt={1} display="flex" alignItems="center" gap={1}>
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">
          Calculating loyalty points...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="warning" sx={{ mt: 1 }}>
        <Typography variant="body2">
          Failed to calculate Société points: {error instanceof Error ? error.message : String(error)}
        </Typography>
      </Alert>
    );
  }

  if (!data?.result) {
    return null;
  }

  const { result } = data;
  const totalPoints = result.totalBasePoints + result.totalBonusPoints;
  const totalStatusCredits = result.totalStatusCredits + result.totalBonusStatusCredits;

  return (
    <Box mt={1}>
      <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
        <Typography variant="subtitle2"> Société Earn: </Typography>
        <Tooltip
          title={
            <Box>
              {result.totalBasePoints > 0 && (
                <Typography variant="caption" display="block">
                  Base Points: {result.totalBasePoints}
                </Typography>
              )}
              {result.totalBonusPoints > 0 && (
                <Typography variant="caption" display="block">
                  Bonus Points: {result.totalBonusPoints}
                </Typography>
              )}
            </Box>
          }
        >
          <Chip label={`${totalPoints} Points`} size="small" color="success" />
        </Tooltip>
        <Tooltip
          title={
            <Box>
              {result.totalStatusCredits > 0 && (
                <Typography variant="caption" display="block">
                  Status Credits: {result.totalStatusCredits}
                </Typography>
              )}
              {result.totalBonusStatusCredits > 0 && (
                <Typography variant="caption" display="block">
                  Bonus Status Credits: {result.totalBonusStatusCredits}
                </Typography>
              )}
            </Box>
          }
        >
          <Chip label={`${totalStatusCredits} SC`} size="small" color="info" />
        </Tooltip>
      </Stack>

      {/* Show create account button if user has no account */}
      {userId && !hasAccount && !accountError && (
        <Box mt={1}>
          <Button
            size="small"
            variant="outlined"
            color="primary"
            startIcon={<PersonAdd />}
            onClick={handleCreateAccount}
            disabled={createAccountMutation.isPending}
          >
            {createAccountMutation.isPending ? 'Creating...' : 'Create Société account to earn'}
          </Button>
        </Box>
      )}

      {/* Show account error if any */}
      {accountError && userId && (
        <Typography variant="caption" color="error" display="block" mt={0.5}>
          Unable to check account status
        </Typography>
      )}
    </Box>
  );
}
